/**
 * Knowledge base articles list, filterable by category/tag, with drag-and-drop per category.
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Empty, Modal, Select, Spin, Col, message } from 'antd';
import {
  LoadingContainer,
  HeaderRow,
  SectionTitle,
  FilterRow,
  ArticlesScrollContainer,
  FlexColumnGap24,
} from '../components/knowledgeBase/KnowledgeBase.styles';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { useKBCategories, useKBTags, useKBArticles, useUpdateArticleOrder, useDeleteArticle } from '../hooks/useKnowledgeBase';
import type { KBArticle } from '@municipality/types';
import { SuperAdminRole } from '@municipality/types';
import { SortableArticleRow, SortableKbList } from '../components/knowledgeBase';

const ROLES_CAN_EDIT_ARTICLE = [SuperAdminRole.SUPER_ADMIN, SuperAdminRole.CONTENT_MANAGER];

function getCategoryName(
  translations: Record<string, { name?: string }>,
  primary: string
): string {
  const p = translations[primary]?.name;
  if (p) return p;
  return translations.en?.name ?? '';
}

function getTagName(
  translations: Record<string, { name?: string }>,
  primary: string
): string {
  const p = translations[primary]?.name;
  if (p) return p;
  return translations.en?.name ?? '';
}

export const KnowledgeBaseArticles: React.FC = () => {
  const { t } = useTranslation(['knowledgeBase', 'common']);
  const navigate = useNavigate();
  const { user } = useAuth();
  const superAdminRole = (user as { superAdminRole?: SuperAdminRole })?.superAdminRole;
  const canEditArticle = !!superAdminRole && ROLES_CAN_EDIT_ARTICLE.includes(superAdminRole);

  const [categoryFilter, setCategoryFilter] = useState<string | undefined>();
  const [tagFilter, setTagFilter] = useState<string | undefined>();
  const [orderedIdsByCategory, setOrderedIdsByCategory] = useState<Record<string, string[]>>({});

  const { data: categories, isLoading: catLoading } = useKBCategories();
  const { data: tags, isLoading: tagLoading } = useKBTags();
  const { data: articles, isLoading: artLoading } = useKBArticles({
    categoryId: categoryFilter,
    status: undefined,
  });
  const updateOrderMutation = useUpdateArticleOrder();
  const deleteMutation = useDeleteArticle();

  const filteredArticles = useMemo(() => {
    let list = articles ?? [];
    if (tagFilter) {
      list = list.filter((a) => a.tagIds?.includes(tagFilter));
    }
    return list;
  }, [articles, tagFilter]);

  const articlesByCategory = useMemo(() => {
    const map: Record<string, typeof filteredArticles> = {};
    filteredArticles.forEach((a) => {
      const cid = a.categoryId ?? '_none';
      if (!map[cid]) map[cid] = [];
      map[cid].push(a);
    });
    Object.keys(map).forEach((cid) => {
      map[cid].sort((x, y) => x.order - y.order);
    });
    return map;
  }, [filteredArticles]);

  const categoryNames = useMemo(() => {
    const names: Record<string, string> = {};
    categories?.forEach((c) => {
      names[c.id] = getCategoryName(c.translations, c.languageSettings.primaryLanguage);
    });
    return names;
  }, [categories]);

  const handleReorder = useCallback(
    async (categoryId: string, newOrder: string[]) => {
      setOrderedIdsByCategory((prev) => ({ ...prev, [categoryId]: newOrder }));
      try {
        await updateOrderMutation.mutateAsync({ categoryId, orderedIds: newOrder });
        message.success(t('knowledgeBase:messages.orderUpdated'));
      } catch {
        message.error(t('knowledgeBase:errors.saveError'));
        setOrderedIdsByCategory((prev) => {
          const next = { ...prev };
          delete next[categoryId];
          return next;
        });
      }
    },
    [updateOrderMutation, t]
  );

  const handleEdit = useCallback((article: { id: string }) => {
    navigate(`/knowledge-base/articles/edit/${article.id}`);
  }, [navigate]);

  const handleDelete = useCallback(
    (article: KBArticle) => {
      const name =
        article.translations?.[article.languageSettings?.primaryLanguage ?? 'en']?.title ??
        article.translations?.en?.title ??
        article.id;
      Modal.confirm({
        title: t('knowledgeBase:messages.confirmDelete'),
        content: t('knowledgeBase:articles.deleteConfirm', { name }),
        okText: t('common:buttons.delete'),
        okType: 'danger',
        cancelText: t('common:buttons.cancel'),
        onOk: async () => {
          try {
            await deleteMutation.mutateAsync(article.id);
            message.success(t('knowledgeBase:messages.articleDeleted'));
          } catch {
            message.error(t('knowledgeBase:errors.deleteError'));
          }
        },
      });
    },
    [deleteMutation, t]
  );

  const isLoading = catLoading || tagLoading || artLoading;

  if (isLoading) {
    return (
      <LoadingContainer>
        <Spin size="large" />
      </LoadingContainer>
    );
  }

  return (
    <div>
      <HeaderRow>
        <SectionTitle>{t('articles.title')}</SectionTitle>
        {canEditArticle && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/knowledge-base/articles/create')}
          >
            {t('articles.create')}
          </Button>
        )}
      </HeaderRow>

      <FilterRow gutter={[16, 16]}>
        <Col>
          <Select
            placeholder={t('articles.category')}
            allowClear
            style={{ minWidth: 200 }}
            value={categoryFilter}
            onChange={setCategoryFilter}
            options={categories?.map((c) => ({
              label: getCategoryName(c.translations, c.languageSettings.primaryLanguage),
              value: c.id,
            }))}
          />
        </Col>
        <Col>
          <Select
            placeholder={t('articles.tags')}
            allowClear
            style={{ minWidth: 200 }}
            value={tagFilter}
            onChange={setTagFilter}
            options={tags?.map((tagItem) => ({
              label: getTagName(tagItem.translations, tagItem.languageSettings.primaryLanguage),
              value: tagItem.id,
            }))}
          />
        </Col>
      </FilterRow>

      {filteredArticles.length === 0 ? (
        <Card>
          <Empty
            description={t('articles.noArticles')}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            {canEditArticle && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate('/knowledge-base/articles/create')}
              >
                {t('articles.create')}
              </Button>
            )}
          </Empty>
        </Card>
      ) : (
        <FlexColumnGap24>
          {Object.entries(articlesByCategory).map(([categoryId, arts]) => {
            const orderedIds = orderedIdsByCategory[categoryId] ?? arts.map((a) => a.id);
            const categoryName = categoryNames[categoryId] ?? categoryId;
            return (
              <Card key={categoryId} title={categoryName}>
                <SortableKbList
                  itemIds={orderedIds}
                  onReorder={canEditArticle ? (ids) => handleReorder(categoryId, ids) : undefined}
                >
                  <ArticlesScrollContainer>
                    {orderedIds.map((id) => {
                      const article = arts.find((a) => a.id === id);
                      if (!article) return null;
                      return (
                        <SortableArticleRow
                          key={article.id}
                          article={article}
                          categoryName={categoryName}
                          onEdit={canEditArticle ? handleEdit : undefined}
                          onDelete={canEditArticle ? handleDelete : undefined}
                          dragEnabled={canEditArticle}
                        />
                      );
                    })}
                  </ArticlesScrollContainer>
                </SortableKbList>
              </Card>
            );
          })}
        </FlexColumnGap24>
      )}
    </div>
  );
};
